class EuroProducts extends HTMLElement {
  connectedCallback() {
    this.mainEl = this.querySelector('[data-products-main]');
    this.fillEl = this.querySelector('[data-products-fill]');
    this.statusEl = this.querySelector('[data-quick-add-status]');
    this.mq = window.matchMedia('(min-width: 1200px)');
    this.onChange = () => this.sync();
    this.onQuickAddClick = (event) => {
      const button = event.target.closest?.('[data-quick-add]');
      if (!button || !this.contains(button)) return;
      this.quickAdd(button);
    };
    this.mq.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onQuickAddClick);
    this.whenReady(() => this.sync());
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onQuickAddClick);
    clearInterval(this.readyTimer);
    this.destroy();
  }

  whenReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }

    this.readyTimer = setInterval(() => {
      if (!window.Swiper) return;
      clearInterval(this.readyTimer);
      callback();
    }, 30);
  }

  /**
   * Zero on purpose. The gap between two cards is no longer Swiper's job: each
   * slide now carries --euro-products-card-gap / 2 of transparent padding on
   * every side, half of it for the gap and half as the room the card's shadow
   * needs inside Swiper's overflow: hidden. Adding the gap here as well would
   * double it.
   */
  spaceBetween() {
    return 0;
  }

  thumbPercent() {
    return this.mq.matches ? 21.7 : 28.99;
  }

  sync() {
    this.destroy();
    this.init();
  }

  updateBar(progress) {
    if (!this.fillEl) return;
    const thumb = this.thumbPercent();
    const shift = (Math.min(Math.max(progress, 0), 1) * (100 - thumb)) / thumb;
    this.fillEl.style.transform = `translateX(${shift * 100}%)`;
  }

  init() {
    if (this.mainSwiper || !this.mainEl || !window.Swiper) return;

    this.mainSwiper = new window.Swiper(this.mainEl, {
      slidesPerView: 'auto',
      spaceBetween: this.spaceBetween(),
      speed: 500,
      watchOverflow: true,
      grabCursor: true,
      on: {
        progress: (swiper, progress) => this.updateBar(progress),
        setTranslate: (swiper) => this.updateBar(swiper.progress),
      },
    });
    this.updateBar(this.mainSwiper.progress || 0);
  }

  destroy() {
    this.mainSwiper?.destroy(true, true);
    this.mainSwiper = null;
  }

  /* -------------------------------------------------------- quick add */

  /**
   * Adds one unit of a single-variant, in-stock product straight from the card.
   *
   * A multi-variant or sold out card never reaches here: Liquid renders those as
   * a "choose options" link and a "sold out" label instead, so there is always a
   * concrete variant id whenever this runs.
   */
  async quickAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.dataset.quickAddBusy === 'true') return;

    const routes = window.routes || {};
    const drawer = document.querySelector('cart-drawer');

    button.dataset.quickAddBusy = 'true';
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const payload = await this.postToCart(variantId);
      this.syncCartBubble(payload);

      // The drawer repaints itself in response to this event, so await it —
      // otherwise the drawer would open showing the pre-add contents.
      await this.notifyCartUpdate(payload, variantId);

      this.announce(this.dataset.quickAddAdded || '');

      if (drawer && typeof drawer.open === 'function') {
        drawer.open(button);
      } else {
        window.location.href = routes.cart_url || '/cart';
      }
    } catch (error) {
      this.announce(error && error.message ? error.message : this.dataset.quickAddError || '');
    } finally {
      delete button.dataset.quickAddBusy;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  async postToCart(variantId) {
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        // Ask Shopify to render just the header count fragment, so the bubble
        // stays in sync even when no cart drawer is present.
        sections: 'cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok) {
      const message =
        (payload && (payload.description || payload.message)) || this.dataset.quickAddError;
      throw new Error(message || '');
    }

    return payload;
  }

  /**
   * The theme's header count bubble lives on an anchor whose inner markup is
   * exactly what sections/cart-icon-bubble.liquid renders, so the fragment can
   * replace it wholesale.
   */
  syncCartBubble(payload) {
    const bubble = document.getElementById('cart-icon-bubble');
    const html = payload && payload.sections && payload.sections['cart-icon-bubble'];
    if (!bubble || !html) return;
    bubble.innerHTML = html;
  }

  /**
   * Dawn's pub/sub is what the cart drawer subscribes to (cart-drawer-items
   * refetches and replaces itself), so publishing keeps us off the drawer's
   * internals. `publish` is a global from pubsub.js; guard in case the theme
   * ever ships without it.
   */
  notifyCartUpdate(payload, variantId) {
    try {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        return Promise.resolve();
      }
      return (
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'euro-products',
          productVariantId: variantId,
          cartData: payload,
        }) || Promise.resolve()
      );
    } catch (error) {
      return Promise.resolve();
    }
  }

  announce(message) {
    if (!this.statusEl) return;
    // Clear first so repeating the same message is still announced.
    this.statusEl.textContent = '';
    window.requestAnimationFrame(() => {
      this.statusEl.textContent = message;
    });
  }
}

customElements.define('euro-products', EuroProducts);
